import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { CA_MONTHS, } from "./customerAcquisition.utils";
// Discrete pink scale (light -> dark) keyed by the API's heat class.
const CLASS_COLORS = {
    L: { bg: "#FDF2FA", text: "#9D174D" }, // lowest
    LM: { bg: "#FCCEEE", text: "#9D174D" },
    M: { bg: "#EE46BC", text: "#FFFFFF" },
    H: { bg: "#C11574", text: "#FFFFFF" }, // highest
};
// Legend order (Low -> High) drives the swatches shown next to the heading.
const LEGEND_ORDER = ["L", "LM", "M", "H"];
const formatNumber = (n) => n.toLocaleString("en-US");
const CustomerAcquisition = ({ rows }) => {
    // Monthly totals: sum each month column vertically across every year.
    const { totals, grandTotal } = useMemo(() => {
        const totals = CA_MONTHS.map((_, monthIdx) => rows.reduce((sum, r) => sum + (r.months[monthIdx]?.value ?? 0), 0));
        const grandTotal = totals.reduce((sum, t) => sum + t, 0);
        return { totals, grandTotal };
    }, [rows]);
    // Map a cell to its background/text colour based on the API's heat class.
    const getHeat = (cell) => {
        if (!cell || cell.heatClass == null)
            return null;
        return CLASS_COLORS[cell.heatClass] ?? null;
    };
    const rowTotal = (r) => r.months.reduce((sum, c) => sum + (c.value ?? 0), 0);
    // Shared column template: Year | 12 months | Total
    const gridTemplate = "120px repeat(12, 1fr) 96px";
    const headerCellSx = {
        fontSize: "12px !important",
        fontWeight: 600,
        color: "#475467",
        textAlign: "center",
        py: 1.5,
    };
    return (_jsxs(Paper, { elevation: 0, sx: { p: 2, border: "1px solid #EAECF0", borderRadius: "10px", bgcolor: "#fff" }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                    flexWrap: "wrap",
                    gap: 1,
                }, children: [_jsx(Typography, { className: "head-Font-Sizes", sx: { fontWeight: 600, color: "#101828" }, children: "Customer Acquisition" }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: "12px !important", color: "#667085" }, children: "Low" }), _jsx(Box, { sx: { display: "flex", gap: "3px" }, children: LEGEND_ORDER.map((c) => (_jsx(Box, { sx: { width: 28, height: 16, borderRadius: "4px", bgcolor: CLASS_COLORS[c].bg } }, c))) }), _jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: "12px !important", color: "#667085" }, children: "High" })] })] }), _jsx(Box, { sx: { overflowX: "auto" }, children: _jsxs(Box, { sx: { minWidth: "900px", maxHeight: 420, overflowY: "auto" }, children: [_jsxs(Box, { className: "all-Font-Sizes-12", sx: {
                                display: "grid",
                                gridTemplateColumns: gridTemplate,
                                borderBottom: "1px solid #EAECF0",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                                bgcolor: "#fff",
                            }, children: [_jsx(Typography, { className: "all-Font-Sizes-12", sx: { ...headerCellSx, textAlign: "left", pl: 1 }, children: "Year" }), CA_MONTHS.map((m) => (_jsx(Typography, { className: "all-Font-Sizes-12", sx: headerCellSx, children: m }, m))), _jsx(Typography, { className: "all-Font-Sizes-12", sx: headerCellSx, children: "Total" })] }), rows.map((row) => (_jsxs(Box, { sx: {
                                display: "grid",
                                gridTemplateColumns: gridTemplate,
                                alignItems: "center",
                                borderBottom: "1px solid #F2F4F7",
                            }, children: [_jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: "12px !important", fontWeight: 600, color: "#101828", pl: 1, py: 1 }, children: row.year }), row.months.map((cell, idx) => {
                                    const heat = getHeat(cell);
                                    return (_jsx(Box, { sx: {
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            px: 0.5,
                                            py: 0.75,
                                        }, children: _jsx(Box, { sx: {
                                                width: "100%",
                                                minHeight: 34,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                borderRadius: "6px",
                                                bgcolor: heat ? heat.bg : "transparent",
                                                color: heat ? heat.text : "#98A2B3",
                                                fontWeight: 600,
                                                fontSize: "13px",
                                            }, children: cell.value != null ? formatNumber(cell.value) : "" }) }, idx));
                                }), _jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: "12px !important", fontWeight: 600, color: "#101828", textAlign: "center", py: 1 }, children: formatNumber(rowTotal(row)) })] }, row.year))), _jsxs(Box, { sx: {
                                display: "grid",
                                gridTemplateColumns: gridTemplate,
                                alignItems: "center",
                                borderTop: "2px solid #EAECF0",
                                bgcolor: "#FCFCFD",
                                position: "sticky",
                                bottom: 0,
                                zIndex: 2,
                            }, children: [_jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: "12px !important", fontWeight: 700, color: "#101828", pl: 1, py: 1.5 }, children: "Total" }), totals.map((t, idx) => (_jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: "12px !important", fontWeight: 600, color: "#344054", textAlign: "center", py: 1.5 }, children: formatNumber(t) }, idx))), _jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: "12px !important", fontWeight: 700, color: "#101828", textAlign: "center", py: 1.5 }, children: formatNumber(grandTotal) })] })] }) })] }));
};
export default CustomerAcquisition;
